import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { 
  ArrowRight, Check, Instagram, CheckCircle,
  Sparkles, MessageSquare, CalendarClock, Star
} from 'lucide-react';

// 🆕 Componente de geração gratuita
import FreeGenerationSection from './free/FreeGenerationSection';

// Hook para detectar quando elemento entra na viewport
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.05, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isInView];
};

const Home = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Refs para animações de scroll
  const [transitionRef, transitionInView] = useInView();
  const [section2Ref, section2InView] = useInView();
  const [section3Ref, section3InView] = useInView();
  const [section4Ref, section4InView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [finalCtaRef, finalCtaInView] = useInView();

  // Features da Section 2
  const features = [
    'Posts ilimitados com Inteligência Artificial',
    'Design criado automaticamente',
    'Publicação automática para Instagram/Facebook',
    'Legendas completas em um clique',
    'Total customização da identidade visual',
  ];

  // Grid de imagens exemplo - ATUALIZADO
  const gridImages = [
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.22.43.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.22.44.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.27.59.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.29.32.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.30.57.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.31.39.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.34.18.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.36.59.jpeg',
    'https://hellosocialstore.blob.core.windows.net/hellosocial/WhatsApp%20Image%202026-02-03%20at%2013.40.21.jpeg',
  ];

  // Steps da Section 3
  const steps = [
    {
      number: 1,
      icon: Sparkles,
      title: 'Criação automática de posts',
      description: 'Nossa IA entende seu negócio, seu público e seu tom de voz para gerar ideias e conteúdos prontos para postar.',
    },
    {
      number: 2,
      icon: MessageSquare,
      title: 'Legendas inteligentes e personalizadas',
      description: 'Legendas com ganchos, CTA, hashtags e storytelling — adaptadas para cada rede social.',
    },
    {
      number: 3,
      icon: CalendarClock,
      title: 'Agendamento de publicações',
      description: 'Planeje sua semana ou mês inteiro em minutos. Crie, organize e publique no piloto automático.',
    },
  ];

  // Depoimentos da Section 4
  const testimonials = [
    {
      name: 'Priscila',
      rating: 5,
      text: '"Fenomenal! Nunca vi nada tão intuitivo, simples de usar e com uma entrega fantástica! Vale cada centavo investido! Super recomendo!"',
    },
    {
      name: 'Aline',
      rating: 5,
      text: '"A experiência que estou tendo tem sido simplesmente incrível. Me fez ganhar tempo, poder ter um lugar claro e eficiente para gerenciar minhas postagens."',
    },
    {
      name: 'Keyla',
      rating: 5,
      text: '"Essa ferramenta é perfeita para quem não tem agilidade em montar e publicar posts! Ainda bem que encontrei o hello, social!"',
    },
    {
      name: 'Michel',
      rating: 5,
      text: '"É simplesmente incrível! Ela não apenas otimiza o tempo dos empreendedores, mas também simplifica todo o processo. É super intuitiva!"',
    },
    {
      name: 'Carolina',
      rating: 5,
      text: '"Mudou completamente a forma como gerencio minhas redes sociais. Agora consigo focar no que realmente importa: meus clientes!"',
    },
    {
      name: 'Rafael',
      rating: 5,
      text: '"Impressionante a qualidade dos posts gerados. Meus seguidores notaram a diferença e o engajamento aumentou muito!"',
    },
  ];

  // Duplicar testimonials para loop infinito
  const duplicatedTestimonials = [...testimonials, ...testimonials];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      
      {/* ============================================
          HEADER - Flutuante com blur transparente
          ============================================ */}
      <header className="fixed top-4 left-4 right-4 z-50">
        <div className="max-w-7xl mx-auto bg-white/50 backdrop-blur-md border border-white/30 rounded-[30px] px-4 sm:px-6 lg:px-8 shadow-lg">
          <div className="flex items-center justify-between h-14">
            
            {/* Logo - Esquerda */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img
                src="/logo.png"
                alt="hello, social"
                className="w-10 h-10 object-contain"
              />
            </Link>

            {/* Menu Central */}
            <nav className="hidden sm:flex items-center gap-8">
              <Link
                to="/"
                className="text-gray-800 hover:text-gray-900 font-medium transition-colors"
              >
                Home
              </Link>
              <Link
                to="/services-landing"
                className="text-gray-800 hover:text-gray-900 font-medium transition-colors"
              >
                Serviços
              </Link>
              <Link
                to="/plans"
                className="text-gray-800 hover:text-gray-900 font-medium transition-colors"
              >
                Planos
              </Link>
            </nav>

            {/* CTA - Direita */}
            <Link
              to={currentUser ? "/contents/new" : "/register"}
              className="px-5 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-semibold rounded-full hover:from-amber-500 hover:to-orange-600 transition-all shadow-md hover:shadow-lg hover:shadow-amber-500/25 text-sm"
            >
              Comece já
            </Link>
          </div>
        </div>
      </header>

      {/* ============================================
          SEÇÃO 1: HERO COM GERAÇÃO GRATUITA
          ============================================ */}
      <section
        className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 sm:py-20 pt-28 sm:pt-32 bg-center bg-cover bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://hellosocialstore.blob.core.windows.net/hellosocial/section_1_hello.png')",
        }}
      >
        {/* Overlay suave para melhorar legibilidade */}
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-white/40 via-white/20 to-white/40" />

        <div className="relative z-10 max-w-4xl mx-auto text-center w-full">
          
          <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
            <img
              src="/logo.png"
              alt="hello, social"
              className="w-20 h-20 sm:w-28 sm:h-28 lg:w-32 lg:h-32 object-contain"
            />
          </div>

          {/* Títulos em preto para contraste */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            Crie conteúdos incríveis{' '}
            <span className="text-gray-900 block sm:inline">
              direto do seu Instagram
            </span>
          </h1>

          <p className="text-base sm:text-lg lg:text-xl text-gray-800 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
            Digite seu @ e deixe a IA criar posts personalizados com a cara da sua marca.
          </p>

          {/* 🆕 COMPONENTE DE GERAÇÃO GRATUITA */}
          {/* Se usuário não está logado, mostra geração gratuita */}
          {/* Se está logado, redireciona para área de criação */}
          {currentUser ? (
            <div className="max-w-xl mx-auto px-4">
              <Link
                to="/contents/new"
                className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-base sm:text-lg rounded-2xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-xl hover:shadow-2xl hover:shadow-amber-500/30 hover:-translate-y-1"
              >
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
                Criar novo conteúdo
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            </div>
          ) : (
            <div className="px-4">
              <FreeGenerationSection />
              {/* Texto de teste grátis - CORRIGIDO PARA BRANCO */}
              <p className="text-white text-sm sm:text-base mt-4 font-medium drop-shadow-lg">
                ✨ Teste grátis — sem cadastro necessário
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ============================================
          TRANSIÇÃO: Título com fade-in
          ============================================ */}
      <section 
        ref={transitionRef}
        className="py-12 sm:py-16 lg:py-20 mt-10 sm:mt-20 px-4 bg-white relative overflow-hidden"
      >
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight transition-all duration-1000 ${
              transitionInView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            Crie. Personalize. Agende. Publique.
          </h2>
          <p 
            className={`text-sm sm:text-base lg:text-lg text-gray-500 max-w-2xl mx-auto transition-all duration-1000 delay-300 ${
              transitionInView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            Tudo isso em segundos — com inteligência artificial trabalhando por você.
          </p>
        </div>
      </section>

      {/* ============================================
          SEÇÃO 2: Posts e Reels para Instagram
          ============================================ */}
      <section 
        ref={section2Ref}
        className="py-12 sm:py-16 lg:py-24 px-4 bg-gradient-to-b from-amber-50/30 to-white"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            
            {/* Texto - Esquerda */}
            <div>
              <h2 
                className={`text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight transition-all duration-700 ${
                  section2InView 
                    ? 'opacity-100 translate-x-0' 
                    : 'opacity-0 -translate-x-8'
                }`}
              >
                Posts e Reels para Instagram em um clique
              </h2>
              
              {/* Bulletpoints com animação sequencial */}
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {features.map((feature, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-start gap-3 sm:gap-4 transition-all duration-500 ${
                      section2InView 
                        ? 'opacity-100 translate-x-0' 
                        : 'opacity-0 -translate-x-8'
                    }`}
                    style={{ 
                      transitionDelay: section2InView ? `${(idx + 1) * 150}ms` : '0ms' 
                    }}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <Link
                to={currentUser ? "/contents/new" : "/register"}
                className={`inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl hover:shadow-amber-500/25 hover:-translate-y-1 text-sm sm:text-base ${
                  section2InView 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4'
                }`}
                style={{ 
                  transitionDelay: section2InView ? '900ms' : '0ms',
                  transitionDuration: '500ms'
                }}
              >
                Criar meu conteúdo
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            </div>

            {/* Grid de Imagens - Direita */}
            <div 
              className={`transition-all duration-1000 delay-300 ${
                section2InView 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-8'
              }`}
            >
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 border border-gray-100">
                {/* Header do card */}
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900">Editor Mágico</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Calendário de Posts</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-xs sm:text-sm font-medium text-emerald-600">Pronto</span>
                  </div>
                </div>

                {/* Grid de imagens */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {gridImages.map((img, idx) => (
                    <div 
                      key={idx}
                      className={`relative aspect-square rounded-lg sm:rounded-xl overflow-hidden bg-gray-100 transition-all duration-500 hover:scale-105 hover:shadow-lg ${
                        section2InView 
                          ? 'opacity-100 scale-100' 
                          : 'opacity-0 scale-95'
                      }`}
                      style={{ 
                        transitionDelay: section2InView ? `${500 + (idx * 100)}ms` : '0ms' 
                      }}
                    >
                      <img
                        src={img}
                        alt={`Post ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {/* Badge de tipo */}
                      <div className="absolute top-1 sm:top-2 right-1 sm:right-2">
                        <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold rounded-md ${
                          idx % 3 === 2 
                            ? 'bg-gradient-to-r from-pink-500 to-orange-500 text-white' 
                            : 'bg-white/90 backdrop-blur text-gray-700'
                        }`}>
                          {idx % 3 === 2 ? 'Reel' : 'Post'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer do card */}
                <div className="flex items-center justify-between mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-100">
                  <span className="text-xs sm:text-sm text-gray-500">9 de 9 posts</span>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-amber-400 rounded-full" />
                    <span className="w-2 h-2 bg-amber-300 rounded-full" />
                    <span className="w-2 h-2 bg-amber-200 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SEÇÃO 3: Funcionalidades - Passo a Passo
          ============================================ */}
      <section 
        ref={section3Ref}
        className="py-12 sm:py-16 lg:py-24 px-4 bg-gradient-to-b from-white via-amber-50/20 to-white"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            
            {/* Cards Passo a Passo - Esquerda */}
            <div>
              <h3 
                className={`text-xs sm:text-sm font-bold text-amber-600 uppercase tracking-wider mb-2 sm:mb-3 transition-all duration-700 ${
                  section3InView 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4'
                }`}
              >
                Como funciona
              </h3>
              <h2 
                className={`text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight transition-all duration-700 delay-100 ${
                  section3InView 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4'
                }`}
              >
                Tudo o que você precisa para criar e publicar conteúdo — automatizado por IA
              </h2>

              {/* Steps */}
              <div className="space-y-3 sm:space-y-4">
                {steps.map((step, idx) => {
                  const Icon = step.icon;
                  const isFirst = idx === 0;
                  
                  return (
                    <div 
                      key={idx}
                      className={`relative transition-all duration-700 ${
                        section3InView 
                          ? 'opacity-100 translate-x-0' 
                          : 'opacity-0 -translate-x-8'
                      }`}
                      style={{ 
                        transitionDelay: section3InView ? `${(idx + 2) * 150}ms` : '0ms' 
                      }}
                    >
      
                      <div 
                        className={`flex items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 ${
                          isFirst 
                            ? 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-amber-500/20' 
                            : 'bg-white border-gray-100 hover:border-amber-200 hover:shadow-md'
                        }`}
                      >
                        {/* Número/Ícone */}
                        <div 
                          className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${
                            isFirst 
                              ? 'bg-white/20' 
                              : 'bg-amber-50'
                          }`}
                        >
                          <span 
                            className={`text-lg sm:text-xl font-bold ${
                              isFirst ? 'text-white' : 'text-amber-600'
                            }`}
                          >
                            {step.number}
                          </span>
                        </div>

                        {/* Conteúdo */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 
                              className={`font-bold text-base sm:text-lg ${
                                isFirst ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {step.title}
                            </h4>
                            {isFirst && (
                              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white/80" />
                            )}
                          </div>
                          <p 
                            className={`text-xs sm:text-sm leading-relaxed ${
                              isFirst ? 'text-white/90' : 'text-gray-600'
                            }`}
                          >
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Texto Destaque - Direita */}
            <div 
              className={`lg:pl-8 transition-all duration-1000 delay-500 ${
                section3InView 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-8'
              }`}
            >
              {/* Ícone decorativo */}
              <div 
                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 sm:mb-6 shadow-lg shadow-amber-500/30 transition-all duration-700 delay-600 ${
                  section3InView 
                    ? 'opacity-100 scale-100' 
                    : 'opacity-0 scale-75'
                }`}
              >
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>

              <h2 
                className={`text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight transition-all duration-700 delay-700 ${
                  section3InView 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8'
                }`}
              >
                Gere o conteúdo completo do seu post em segundos
              </h2>
              
              <p 
                className={`text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed transition-all duration-700 delay-800 ${
                  section3InView 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8'
                }`}
              >
                Não perca mais tempo pensando em o que postar. Nossa IA analisa seu perfil e cria 
                conteúdos que conversam com seu público.
              </p>

              {/* CTA secundário */}
              <Link
                to={currentUser ? "/contents/new" : "/register"}
                className={`inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 text-sm sm:text-base ${
                  section3InView 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4'
                }`}
                style={{ 
                  transitionDelay: section3InView ? '900ms' : '0ms',
                  transitionDuration: '500ms'
                }}
              >
                Começar agora
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SEÇÃO SERVIÇOS: Serviços Profissionais
          ============================================ */}
      <section
        ref={servicesRef}
        className="py-12 sm:py-16 lg:py-24 px-4 bg-gradient-to-b from-amber-50/30 to-white"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4"
              style={{
                opacity: servicesInView ? 1 : 0,
                transform: servicesInView ? 'translateY(0)' : 'translateY(30px)',
                transition: 'all 0.6s ease-out'
              }}
            >
              Serviços Profissionais
            </h2>
            <p
              className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto"
              style={{
                opacity: servicesInView ? 1 : 0,
                transform: servicesInView ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.6s ease-out 0.2s'
              }}
            >
              Além da criação de conteúdo com IA, oferecemos serviços completos de marketing digital.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
            {[
              { icon: '🎨', title: 'Identidade Visual', desc: 'Logo, cores e tipografia para sua marca' },
              { icon: '📈', title: 'Tráfego Pago', desc: 'Campanhas no Instagram, Facebook e Google' },
              { icon: '✨', title: 'Branding', desc: 'Posicionamento e comunicacao de marca' },
              { icon: '🎯', title: 'Planejamento', desc: 'Estratégia e calendário editorial' },
            ].map((s, idx) => (
              <div
                key={s.title}
                className="bg-white rounded-2xl border border-gray-200 p-6 text-center hover:shadow-lg transition-all"
                style={{
                  opacity: servicesInView ? 1 : 0,
                  transform: servicesInView ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.5s ease-out ${0.3 + idx * 0.1}s`
                }}
              >
                <div className="text-3xl mb-3">{s.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
          <div
            className="text-center"
            style={{
              opacity: servicesInView ? 1 : 0,
              transition: 'opacity 0.6s ease-out 0.7s'
            }}
          >
            <Link
              to={currentUser ? '/services' : '/register'}
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-2xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl"
            >
              {currentUser ? 'Conhecer Serviços' : 'Comece Agora'}
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================
          SEÇÃO 4: Depoimentos - Carrossel Infinito
          ============================================ */}
      <section 
        ref={section4Ref}
        className="py-12 sm:py-16 lg:py-24 px-4 bg-gradient-to-b from-white to-amber-50/30 overflow-hidden"
      >
        <div className="max-w-6xl mx-auto mb-8 sm:mb-12 text-center">
          <h2 
            className={`text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 transition-all duration-700 ${
              section4InView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            O que nossos usuários dizem
          </h2>
          <p 
            className={`text-sm sm:text-base text-gray-600 max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              section4InView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            Milhares de empreendedores já transformaram sua presença digital
          </p>
        </div>

        {/* Carrossel Infinito */}
        <div 
          className={`relative transition-all duration-1000 delay-400 ${
            section4InView 
              ? 'opacity-100' 
              : 'opacity-0'
          }`}
        >
          <div className="flex testimonial-scroll">
            {duplicatedTestimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 w-72 sm:w-80 mx-2 sm:mx-3"
              >
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 h-full">
                  {/* Rating */}
                  <div className="flex gap-1 mb-3 sm:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                        <Star 
                          key={i} 
                          className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-400 text-amber-400" 
                        />
                    ))}
                  </div>

                  {/* Texto */}
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                    {testimonial.text}
                  </p>

                  {/* Nome */}
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">
                    — {testimonial.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CSS para animação contínua */}
        <style jsx>{`
          @keyframes scroll-left {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .testimonial-scroll {
            animation: scroll-left 30s linear infinite;
          }
        `}</style>
      </section>

      {/* ============================================
          SEÇÃO 5: PLANOS (ATUALIZADA)
          ============================================ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 bg-black" style={{
          backgroundImage:
            "url('https://hellosocialstore.blob.core.windows.net/hellosocial/last_section.png')",
        }}>
          
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 items-start">
            {/* Título - Esquerda */}
            <div className="lg:pr-8">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Planos para{' '}
                <span className="text-yellow-300">todas as necessidades</span>
              </h2>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mb-4 sm:mb-6">
                Cresça no seu ritmo com planos feitos para acompanhar seu desenvolvimento.
              </p>
              <Link
                to="/plans"
                className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold transition-colors text-sm sm:text-base"
              >
                Ver todos os planos
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Card 1 - Comece gratuitamente */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-xl">
              <div className="flex items-center gap-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Comece gratuitamente
                </h3>
              </div>
              
              <div className="space-y-2 sm:space-y-3 mb-5 sm:mb-6">
                {[
                  '2 créditos grátis por mês (2 imagens)',
                  'Sem cartão de crédito',
                  'Templates básicos',
                  'Legendas automáticas'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/register?plan=free"
                className="block w-full text-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg text-sm"
              >
                Começar grátis
              </Link>
            </div>

            {/* Card 2 - Planos de assinatura */}
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-5 sm:p-6 shadow-xl relative overflow-hidden">
              {/* Elemento decorativo */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              
              <div className="relative z-10">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                  Planos de assinatura a partir de
                </h3>
                <div className="mb-3 sm:mb-4">
                  <span className="text-2xl sm:text-3xl font-bold text-white">R$ 39,90</span>
                  <span className="text-white/90 text-base sm:text-lg"> / mês</span>
                </div>
                
                <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-5 sm:mb-6">
                  Quer ir além? Faça upgrade quando quiser e ganhe mais créditos, recursos e suporte.
                </p>

                <Link
                  to="/plans"
                  className="block w-full text-center px-6 py-3 bg-white text-amber-600 font-bold rounded-xl hover:bg-gray-50 transition-all shadow-md hover:shadow-lg text-sm"
                >
                  Veja todos os planos
                </Link>
              </div>
            </div>
          </div>
        </div>
   
        {/* CTA Final */}
        <div ref={finalCtaRef} className="relative z-10 max-w-4xl mx-auto text-center mt-16 sm:mt-24 lg:mt-40">
          {/* Título em branco para contraste */}
          <h2 
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 transition-all duration-700 ${
              finalCtaInView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            E agora, o que vamos{' '}
            <span className="text-white">
              criar
            </span>
            ?
          </h2>

          {/* Botão para Planos */}
          <Link
            to="/plans"
            className={`inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-base sm:text-lg rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-xl hover:shadow-2xl hover:shadow-amber-500/30 hover:-translate-y-1 ${
              finalCtaInView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
            }`}
            style={{ 
              transitionDelay: finalCtaInView ? '300ms' : '0ms',
              transitionDuration: '500ms'
            }}
          >
            Ver planos
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
        </div>
      </section>

      {/* ============================================
          FOOTER SIMPLES
          ============================================ */}
      <footer className="py-6 sm:py-8 px-4 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="hello, social"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
            />
          </div>
          
          <p className="text-xs sm:text-sm text-gray-500 text-center">
            © 2026 hello, social. Todos os direitos reservados.
          </p>

          <div className="flex items-center gap-4 sm:gap-6">
            <Link to="/plans" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors">
              Planos
            </Link>
            <Link to="/terms" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors">
              Termos
            </Link>
            <Link to="/privacy" className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors">
              Privacidade
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;